import { Center, Flex, Stack, Tooltip } from "@mantine/core";
import { forwardRef, useCallback, useMemo } from "react";
import {
    type ChatTask,
    selectChatAutopilot,
    selectChatNeglection,
    selectUnreadCount,
    useActiveChatMessages,
    useChatStore,
} from "@/sdk/agent/chat";
import { getSessionByTaskId } from "@/sdk/sessions/getters";
import classes from "../chat-components.module.css";
import { ChatAvatar } from "./ChatAvatar";
import { ChatPreviewBuilder } from "./ChatPreviewBuilder";
import { NotificationTimerRing } from "./NotificationTimerRing";
import { QueuedChatIndicator } from "./QueuedChatIndicator";
import { SelectedChatIndicator } from "./SelectedChatIndicator";
import { getChatVariant, getColorByVariant } from "./variants";

export interface QueuedChatProps {
    chat: ChatTask;
    onChatSelected: (chat: ChatTask) => void;
    isChatSelected: boolean;
}

export const QueuedChat = forwardRef<HTMLDivElement, QueuedChatProps>(
    ({ onChatSelected, chat, isChatSelected, ...rest }, ref) => {
        const messages = useActiveChatMessages(chat);
        const lastMessageDate = messages.length > 0 ? messages[messages.length - 1]?.dateCreated : null;
        const { changeStateAt, stateLastChangedAt } = chat;
        const unreadMessageCount = useChatStore(selectUnreadCount(chat.chatId));

        // Subscribe to neglection and autopilot state to trigger re-renders for concurrent chats
        const neglectionState = useChatStore(selectChatNeglection(chat.chatId));
        const autopilotState = useChatStore(selectChatAutopilot(chat.chatId));

        // Recalculate variant when neglection or autopilot state changes
        const variant = useMemo(() => getChatVariant(chat), [chat, neglectionState, autopilotState]);
        const variantColor = useMemo(() => getColorByVariant(variant), [variant]);

        /** Memoized session ID that only changes when the task ID changes */
        const sessionId = useMemo(() => {
            const session = getSessionByTaskId(chat.id);
            if (!session) {
                throw new Error(`Session not found for task ID: ${chat.id} in chat queue`); // should be an impossible case
            }
            return session.id;
        }, [chat.id]);

        const getRingTimerProps = useCallback(() => {
            if (changeStateAt && stateLastChangedAt) {
                const totalTimerDuration = changeStateAt.getTime() - stateLastChangedAt.getTime();
                return {
                    initialPercentage: Math.floor(
                        (100 * (Date.now() - stateLastChangedAt.getTime())) / totalTimerDuration,
                    ),
                    duration: changeStateAt.getTime() - Date.now(),
                };
            }
            return { initialPercentage: undefined, duration: undefined };
        }, [changeStateAt, stateLastChangedAt]);

        const isPending = chat.status === "pending";

        return (
            <Tooltip
                label={isPending ? "This chat cannot be selected until the customer is connected" : undefined}
                position="right"
                withArrow
                disabled={!isPending}
                transitionProps={{ duration: 300 }}
            >
                <Flex
                    ref={ref}
                    id={`chat-select-chatId:${chat.chatId || "ERROR_UNDEFINED_CHAT_ID"}`}
                    {...rest}
                    align={"center"}
                    className={classes.queued_chat_interactive_area}
                    onClick={isPending ? undefined : () => onChatSelected(chat)}
                    data-selected={isChatSelected}
                    data-pending={isPending}
                >
                    <SelectedChatIndicator isChatSelected={isChatSelected} />
                    <NotificationTimerRing
                        variantColor={variantColor}
                        withRippleAnimation={variant === "neglected"} //TODO: support pre-neglect-warning phase in [EWPM-175]
                        ring={{ size: 73, thickness: 3.5, scaleRatio: 1 }}
                        timer={getRingTimerProps()}
                    >
                        <Center>
                            <QueuedChatIndicator
                                color="asurion.5"
                                size={20}
                                label={unreadMessageCount}
                                visible={variant !== "incoming" && !!unreadMessageCount}
                            >
                                <ChatAvatar
                                    sessionId={sessionId}
                                    customerName={chat.customerName}
                                    variant={variant}
                                    variantColor={variantColor}
                                    avatarSize={48}
                                />
                            </QueuedChatIndicator>
                        </Center>
                    </NotificationTimerRing>
                    <ChatPreviewBuilder
                        chat={chat}
                        lastMessageDate={lastMessageDate}
                        variant={variant}
                        variantColor={variantColor}
                        wrapper={Stack}
                    />
                </Flex>
            </Tooltip>
        );
    },
);

QueuedChat.displayName = "QueuedChat";
